import styled from '@emotion/styled';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TextEntry, ThemeSummary } from '../../../types';
import { useRafResizeObserver } from '../../../hooks/useRafResizeObserver';
import { CellStack, Chip, Chips, Muted, SmallButton } from '../styles';

export const ThemeChips = ({ themes }: { themes: Pick<ThemeSummary, 'label'>[] }) => {
  const { t } = useTranslation('newReport');
  const [expanded, setExpanded] = useState(false);
  if (!themes.length) return <Muted title={t('table.noThemes')}>—</Muted>;
  return (
    <Chips>
      {(expanded ? themes : themes.slice(0, 2)).map((theme) => (
        <ThemeChip key={theme.label} title={theme.label} expanded={expanded}>
          {theme.label}
        </ThemeChip>
      ))}
      {themes.length > 2 && (
        <SmallButton type="button" onClick={() => setExpanded(!expanded)}>
          {expanded ? t('table.showLess') : `+${themes.length - 2}`}
        </SmallButton>
      )}
    </Chips>
  );
};

const ExpandableText = ({ text }: { text: string }) => {
  const { t } = useTranslation('newReport');
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowed, setOverflowed] = useState(false);
  const measureOverflow = useCallback((element: HTMLDivElement) => {
    setOverflowed(element.scrollHeight > element.clientHeight + 1);
  }, []);
  useRafResizeObserver(ref, measureOverflow, { enabled: !expanded, remeasureKey: text });
  return (
    <div>
      <TextPreview ref={ref} expanded={expanded}>
        {text}
      </TextPreview>
      {(overflowed || expanded) && (
        <SmallButton type="button" onClick={() => setExpanded(!expanded)}>
          {t(expanded ? 'table.showLess' : 'table.showMore')}
        </SmallButton>
      )}
    </div>
  );
};

export const TextCell = ({ entries }: { entries: TextEntry[] }) => {
  const { t } = useTranslation('newReport');
  const [limit, setLimit] = useState(2);
  if (!entries.length) return <Muted title={t('table.noAnswers')}>—</Muted>;
  return (
    <CellStack>
      {entries.slice(0, limit).map((entry) => (
        <CellStack key={entry.message.id}>
          <ExpandableText text={entry.message[entry.source] ?? ''} />
          {entry.themes.length > 0 && <ThemeChips themes={entry.themes.map((label) => ({ label }))} />}
        </CellStack>
      ))}
      {entries.length > limit && (
        <SmallButton type="button" onClick={() => setLimit((previous) => previous + 10)}>
          {t('table.moreAnswers', { count: Math.min(10, entries.length - limit) })}
        </SmallButton>
      )}
      {limit > 2 && (
        <SmallButton type="button" onClick={() => setLimit(2)}>
          {t('table.showLess')}
        </SmallButton>
      )}
    </CellStack>
  );
};

const ThemeChip = styled(Chip)<{ expanded: boolean }>`
  max-width: ${({ expanded }) => (expanded ? '100%' : 'min(180px, 100%)')};
  ${({ expanded }) => !expanded && `white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`}
`;

const TextPreview = styled.div<{ expanded: boolean }>`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  ${({ expanded }) =>
    !expanded &&
    `
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow: hidden;
  `}
`;
